import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild
}                         from '@angular/core';
import { DeviceTypeEnum } from '@appConfig/media-config';
import { BaseComponent }  from '@core/bases/base-component/base.component';
import {
	IKeyValueMap,
	IScrollData
}                         from '@core/interfaces/common.interfaces';
import { IMedia }         from '@core/interfaces/media-interfaces';
import {
	IModalBaseEvent,
	IModalBaseEventProperties,
	ModalAlignContentItemsEnum,
	ModalBaseEventTypeEnum,
	ModalHeaderContentTypeEnum,
	ModalIdentifierEnum
}                         from '@core/modals/modal.model';
import { fadeInOut }      from '@shared/animations/fadeInOut';
import { takeUntil }      from 'rxjs/operators';

@Component( {
	selector: 'app-modal-base[modalIdentifier]',
	templateUrl: './modal-base.component.html',
	styleUrls: [ './modal-base.component.scss' ],
	animations: [ fadeInOut ]
} )
export class ModalBaseComponent extends BaseComponent implements AfterViewInit, OnInit {

	// private helpers
	private previousContentAlignment: ModalAlignContentItemsEnum;
	private previousDeviceType: DeviceTypeEnum;
	private restoreContentAlignment: ModalAlignContentItemsEnum;

	private _hideModalFooter = false;
	private _hideModalHeader = false;
	private _hideModalPreFooter = true;
	private _hideModalSubheader = true;
	private _showLoader = false;
	private _showProjectedModalOverlay = false;

	/**
	 * Computed field which determines whether to show whole modal overlay itself - regardless it's desired content
	 * which can be 'loader' or 'projected content'. Usually computed from {@link showLoader} and {@link showProjectedModalOverlay}.
	 *
	 * Modal overlay is common for loader and projected content and these two items can be turned on/off individually.
	 */
	showModalContentOverlay = false;

	modalHeaderContentTypeEnum = ModalHeaderContentTypeEnum;

	// obligated
	@Input() headerTitle: string;

	// optional
	@Input() modalHeaderContentType = ModalHeaderContentTypeEnum.VALUE;  // content type of header - string value for title or any projected content
	@Input() modalIdentifier: ModalIdentifierEnum;

	// sections render ###########
	@Input() renderModalAside = true;
	@Input() renderModalContentOverlay = true;
	@Input() renderModalFooter = true;
	@Input() renderModalHeader = true;
	@Input() renderModalHeaderCloseButton = true;
	@Input() renderModalPreFooter = true;
	@Input() renderModalSubheader = true;


	// sections styleClasses ###########
	@Input() modalContentOverlayStyleClasses = '';
	@Input() modalMainStyleClasses = '';
	@Input() modalMainStyleClassMap: IKeyValueMap<boolean>;
	@Input() modalSubheaderStyleClasses = '';


	// sections visibility #######
	// header
	get hideModalHeader(): boolean {
		return this._hideModalHeader;
	}

	@Input() set hideModalHeader( hideModalHeader: boolean ) {
		this._hideModalHeader = hideModalHeader;
		this.updateGridLayout();
	}

	// subheader
	get hideModalSubheader(): boolean {
		return this._hideModalSubheader;
	}

	@Input() set hideModalSubheader( hideModalSubheader: boolean ) {
		this._hideModalSubheader = hideModalSubheader;
		this.updateGridLayout();
	}

	// aside
	@Input() hideModalAside = false;

	// pre-footer
	get hideModalPreFooter(): boolean {
		return this._hideModalPreFooter;
	}

	@Input() set hideModalPreFooter( hideModalPreFooter: boolean ) {
		this._hideModalPreFooter = hideModalPreFooter;
		this.updateGridLayout();
	}

	// footer
	get hideModalFooter(): boolean {
		return this._hideModalFooter;
	}

	@Input() set hideModalFooter( hideModalFooter: boolean ) {
		this._hideModalFooter = hideModalFooter;
		this.updateGridLayout();
	}

	get showProjectedModalOverlay(): boolean {
		return this._showProjectedModalOverlay;
	}

	/**
	 * Show content in the modal overlay.
	 *
	 * @param showProjectedModalOverlay true - show content, false - no content show
	 */
	@Input() set showProjectedModalOverlay( showProjectedModalOverlay: boolean ) {
		const timeout = setTimeout( () => {
			clearTimeout( timeout );
			this._showProjectedModalOverlay = showProjectedModalOverlay;
			this.showModalContentOverlay = showProjectedModalOverlay || this.showLoader;
			this.updateModalContentOverlayProperties();
		}, 0 );
	}

	get showLoader(): boolean {
		return this._showLoader;
	}

	/**
	 * Show loader in the modal overlay.
	 *
	 * @param showLoader true - show loader, false - don't show loader
	 */
	@Input() set showLoader( showLoader: boolean ) {
		this._showLoader = showLoader;
		this.showModalContentOverlay = showLoader || this.showProjectedModalOverlay;
		this.updateModalContentOverlayProperties();
	}

	// views #####################
	@ViewChild( 'modalBody', { static: true } ) modalBodyRef: ElementRef<HTMLDivElement>;

	// others ####################
	@Input() contentAlignment = ModalAlignContentItemsEnum.AS_COLUMNS;

	@Input() requestInProgress: boolean;

	@Output() modalBaseEvent = new EventEmitter<IModalBaseEvent>();

	// template helpers
	gridLayout = '';
	alignContentItemsEnum = ModalAlignContentItemsEnum;
	modalOverlayOffsetTop = '0px';
	modalOverlayOffsetHeight = '0px';
	mainContentIsScrolled = false;

	constructor(
		private readonly changeDetectorRef: ChangeDetectorRef
	) {
		super();
	}

	private emitModalEvent( eventType: ModalBaseEventTypeEnum, properties: IModalBaseEventProperties = {} ): void {
		this.modalBaseEvent.emit( {
			event: eventType,
			...properties
		} );
	}

	private updateGridLayout(): void {
		this.gridLayout = 'grid';
		this.gridLayout += this.renderModalHeader && !this.hideModalHeader ? '--header' : '';
		this.gridLayout += this.renderModalSubheader && !this.hideModalSubheader ? '--subheader' : '';
		this.gridLayout += this.renderModalPreFooter && !this.hideModalPreFooter ? '--pre-footer' : '';
		this.gridLayout += this.renderModalFooter && !this.hideModalFooter ? '--footer' : '';
	}

	private updateModalContentOverlayProperties(): void {
		if ( this.showModalContentOverlay && this.modalBodyRef ) {
			const modalBodyElement = this.modalBodyRef.nativeElement;
			this.modalOverlayOffsetTop = `${ modalBodyElement.offsetTop ?? 0 }px`;
			this.modalOverlayOffsetHeight = `${ modalBodyElement.offsetHeight ?? 0 }px`;
		}
	}

	ngOnInit(): void {
		this.restoreContentAlignment = this.contentAlignment;
		this.previousContentAlignment = this.contentAlignment;
		this.updateGridLayout();

		this.uiService.getMediaData$()
		.pipe( takeUntil( this.ngUnsubscribe ) )
		.subscribe( ( mediaData: IMedia ) => {
			this.updateModalContentOverlayProperties();

			if ( this.previousDeviceType !== mediaData?.device?.type ) {

				if ( mediaData?.device?.type === DeviceTypeEnum.MOBILE ) {
					this.contentAlignment = ModalAlignContentItemsEnum.AS_ROWS;
				// do not change alignment if previous device type is tablet or desktop
				} else if ( ![ DeviceTypeEnum.DESKTOP, DeviceTypeEnum.TABLET ].includes( this.previousDeviceType ) ) {
						this.contentAlignment = this.restoreContentAlignment;
				}

				this.previousDeviceType = mediaData?.device?.type;

				if ( this.previousContentAlignment !== this.contentAlignment ) {
					this.previousContentAlignment = this.contentAlignment;
					const properties: IModalBaseEventProperties = {
						contentAlignment: this.contentAlignment
					};

					this.emitModalEvent( ModalBaseEventTypeEnum.CONTENT_ALIGNMENT_CHANGE, properties );
				}
			}
		} );
	}

	ngAfterViewInit(): void {
		this.updateModalContentOverlayProperties();
		this.changeDetectorRef.detectChanges();
	}

	close(): void {
		this.emitModalEvent( ModalBaseEventTypeEnum.CLOSE );
	}

	toggleHeaderVisibility(): void {
		this.hideModalHeader = !this.hideModalHeader;
		this.hideModalHeader
		? this.emitModalEvent( ModalBaseEventTypeEnum.HEADER_HIDE )
		: this.emitModalEvent( ModalBaseEventTypeEnum.HEADER_SHOW );

		this.updateGridLayout();
	}

	toggleSubheaderVisibility(): void {
		this.hideModalSubheader = !this.hideModalSubheader;
		this.hideModalSubheader
		? this.emitModalEvent( ModalBaseEventTypeEnum.SUBHEADER_HIDE )
		: this.emitModalEvent( ModalBaseEventTypeEnum.SUBHEADER_SHOW );

		this.updateGridLayout();
	}

	toggleAsideVisibility(): void {
		this.hideModalAside = !this.hideModalAside;

		this.hideModalAside
		? this.emitModalEvent( ModalBaseEventTypeEnum.ASIDE_HIDE )
		: this.emitModalEvent( ModalBaseEventTypeEnum.ASIDE_SHOW );
	}

	togglePreFooterVisibility(): void {
		this.hideModalPreFooter = !this.hideModalPreFooter;
		this.hideModalPreFooter
		? this.emitModalEvent( ModalBaseEventTypeEnum.PRE_FOOTER_HIDE )
		: this.emitModalEvent( ModalBaseEventTypeEnum.PRE_FOOTER_SHOW );

		this.updateGridLayout();
	}

	toggleFooterVisibility(): void {
		this.hideModalFooter = !this.hideModalFooter;
		this.hideModalFooter
		? this.emitModalEvent( ModalBaseEventTypeEnum.FOOTER_HIDE )
		: this.emitModalEvent( ModalBaseEventTypeEnum.FOOTER_SHOW );

		this.updateGridLayout();
	}


	toggleContentItemsAlignment(): void {
		this.contentAlignment = this.contentAlignment === ModalAlignContentItemsEnum.AS_ROWS
														? ModalAlignContentItemsEnum.AS_COLUMNS
														: ModalAlignContentItemsEnum.AS_ROWS;

		const properties: IModalBaseEventProperties = {
			contentAlignment: this.contentAlignment
		};

		this.emitModalEvent( ModalBaseEventTypeEnum.CONTENT_ALIGNMENT_CHANGE, properties );
	}

	onScroll( scrollData: IScrollData ) {
		this.mainContentIsScrolled = !!scrollData.scrollTop;

		const properties: IModalBaseEventProperties = {
			contentBodyScrollData: scrollData
		};

		this.emitModalEvent( ModalBaseEventTypeEnum.CONTENT_SCROLL, properties );
	}
}
